import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";

export interface TimelineData {
  id: number;
  orden: number;
  titulo: string;
  descripcion: string;
  fecha: string;
  estado_tipo: string | null;
  estado_color: string | null;
}

export function useTimeline(fallback: TimelineData[] = []) {
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;

  const [data, setData] = useState<TimelineData[]>(fallbackRef.current);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  const fetchData = useCallback(async () => {
    if (!mounted.current) return;
    setLoading(true);
    setError(null);
    try {
      const { data: result, error: err } = await supabase
        .from("timeline")
        .select("*")
        .order("orden", { ascending: true });
      if (err) throw err;
      if (mounted.current) {
        setData((result as TimelineData[]) ?? fallbackRef.current);
      }
    } catch {
      if (mounted.current) {
        setError("Error cargando timeline");
        setData(fallbackRef.current);
      }
    } finally {
      if (mounted.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    fetchData();

    // Suscripción en tiempo real para reflejar cambios del admin sin refresh
    const channel = supabase
      .channel("timeline_realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "timeline" },
        () => {
          if (mounted.current) {
            fetchData();
          }
        }
      )
      .subscribe();

    return () => {
      mounted.current = false;
      supabase.removeChannel(channel);
    };
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}